import { useCallback, useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  GoogleReCaptcha,
  GoogleReCaptchaProvider
} from 'react-google-recaptcha-v3'
import CONSTANTS from '@/constants'
import TextField from '@/components/TextField'
import useAlert from '@/components/Alert/hooks/useAlert'
import { Button, Box, Typography } from '@mui/material'
import useAccount from '@/concepts/Account/hooks/useAccount'
import PasswordStrengthIndicator from '@/components/PasswordStrength'
import { isPasswordValid } from '@/lib/passwordValidation'

const ResetPassword = ({ intent }: { intent: 'reset' | 'activate' }) => {
  const { alertError } = useAlert()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const { resetPassword } = useAccount()

  useEffect(() => {
    const tokenFromParams = searchParams.get('token')
    if (!tokenFromParams) {
      alertError(
        'Invalid or missing reset token. Please request a new password reset.'
      )
      navigate('/forgot-password')
      return
    }
    setToken(tokenFromParams)
  }, [searchParams, navigate, alertError])

  const onVerify = useCallback((token: string) => {
    setRecaptchaToken(token)
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    setLoading(true)
    resetPassword(
      {
        password,
        confirmPassword,
        token: token as string,
        recaptchaToken
      },
      () => navigate('/login'),
      () => setLoading(false)
    )
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={CONSTANTS.RECAPTCHA_V3_SITE_KEY}>
      <Box
        sx={{
          maxWidth: 400,
          mx: 'auto',
          mt: 8,
          p: 4,
          boxShadow: 2,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h5" align="center" gutterBottom>
          {intent === 'reset' ? 'Reset Password' : 'Welcome to StackCommerce'}
        </Typography>
        <Typography variant="body2" align="center" sx={{ mb: 2 }}>
          {intent === 'reset'
            ? 'Enter your new password below.'
            : 'Enter a password below. This will be your password for the StackCommerce Partners platform.'}
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label="New Password"
            value={password}
            onChange={setPassword}
            type="password"
            fullWidth
            sx={{ mb: 2 }}
          />
          <PasswordStrengthIndicator password={password} />
          <TextField
            label="Confirm New Password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            type="password"
            fullWidth
            sx={{ mb: 2 }}
          />
          <GoogleReCaptcha onVerify={onVerify} refreshReCaptcha />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={
              loading ||
              !password ||
              !confirmPassword ||
              !isPasswordValid(password)
            }
          >
            {loading
              ? 'Submitting...'
              : intent === 'reset'
                ? 'Reset Password'
                : 'Activate Account'}
          </Button>
        </form>
      </Box>
    </GoogleReCaptchaProvider>
  )
}

export default ResetPassword
